import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import apiClient from "../../api/apiClient";
import { useSelector } from "react-redux";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { Plus, User, Save, X, Trash2 } from "lucide-react";
import { languageSettingsData } from "../../core/json/languageSettings";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReactSelect } from "@/components/ui/react-select";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

const EditEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [employee, setEmployee] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    language: "",
    status: "",
    storeAssignments: [],
    deletedAssignments: [], // Track assignments to be deleted
  });

  const [showDesignationModal, setShowDesignationModal] = useState(false);
  const [designationFormData, setDesignationFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });

  // Queries
  const {
    data: employeeData,

    isFetching: isFetchingEmployee,
  } = useQuery({
    queryKey: ["employee", id],
    queryFn: async () => {
      const res = await apiClient.get(`${API_ENDPOINTS.USERS.EMPLOYEES}/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
  });

  const { data: designations = [] } = useQuery({
    queryKey: ["designations"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.DESIGNATIONS);
      return res.data.data || [];
    },
    enabled: hasPermission("employee designations read"),
  });

  const storeOptions = stores.map((store) => ({
    value: String(store.id),
    label: store.name,
  }));

  const designationOptions = designations.map((designation) => ({
    value: String(designation.id),
    label: designation.name,
  }));

  const { data: allStoreAssignments = [] } = useQuery({
    queryKey: ["store_assignments"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORE_EMPLOYEE);
      return res.data.data || [];
    },
    enabled: hasPermission("store employee read"),
  });

  useEffect(() => {
    if (employeeData) {
      const assignments = allStoreAssignments.filter(
        (a) => a.employee_id === employeeData.id, // Use employeeData.id for consistency
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEmployee({
        ...employeeData,
        storeAssignments: assignments,
        deletedAssignments: [],
      });
    }
  }, [employeeData, allStoreAssignments]); // Removed 'id' from dependency array as employeeData already contains it

  const saveStoreAssignments = async ({ employeeId, assignments }) => {
    try {
      const updateRequests = assignments
        .filter((assignment) => assignment.id)
        .map((assignment) =>
          apiClient.put(
            `${API_BASE_URL}/store_employee/${assignment.id}`,
            {
              ...assignment,
              role_id: null, // role_id is not used in this context, setting to null
            },
          ),
        );

      const addRequests = assignments
        .filter((assignment) => !assignment.id)
        .map((assignment) =>
          apiClient.post(
            `${API_BASE_URL}/store_employee`,
            { ...assignment, employee_id: employeeId, role_id: null }, // role_id is not used
          ),
        );

      await Promise.all([...updateRequests, ...addRequests]);
      queryClient.invalidateQueries({ queryKey: ["store_assignments"] });
    } catch (error) {
      console.error("Error saving store assignments:", error);
      toast.error("Failed to save store assignments");
    }
  };

  const deleteStoreAssignments = async (assignmentIds) => {
    try {
      await Promise.all(
        assignmentIds.map((assignmentId) =>
          apiClient.delete(`${API_BASE_URL}/store_employee/${assignmentId}`),
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["store_assignments"] });
      toast.success("Store assignments removed successfully");
    } catch (error) {
      console.error("Error deleting store assignments:", error);
      toast.error("Failed to remove store assignments");
    }
  };

  // Mutations
  const updateEmployeeMutation = useMutation({
    mutationFn: async (updatedEmployee) => {
      // 1. Update basic info
      const employeeRes = await apiClient.put(
        `${API_BASE_URL}/employee/${id}`,
        updatedEmployee,
      );

      // 2. Handle assignments (add/update and delete)
      // Filter out assignments that don't have required fields yet
      const validAssignments = updatedEmployee.storeAssignments.filter(
        (a) => a.store_id && a.employee_designation_id,
      );

      return {
        employee: employeeRes.data.data,
        assignments: validAssignments,
        deletedAssignments: updatedEmployee.deletedAssignments,
      };
    },
    onSuccess: (data) => {
      if (data.deletedAssignments && data.deletedAssignments.length > 0) {
        deleteStoreAssignments(data.deletedAssignments);
      }
      saveStoreAssignments({
        employeeId: id,
        assignments: data.assignments,
      });

      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employee", id] });
      toast.success("Employee updated successfully");
      setTimeout(() => {
        navigate(all_routes.users);
      }, 1500);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to update employee");
    },
  });

  const createDesignationMutation = useMutation({
    mutationFn: async (newDesignation) => {
      const res = await apiClient.post(
        `${API_BASE_URL}/employee_designation`,
        newDesignation,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["designations"] });
      toast.success("Designation created successfully!");
      setDesignationFormData({
        name: "",
        description: "",
        status: "active",
      });
      setShowDesignationModal(false);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to create designation",
      );
    },
  });

  const handleChange = (e) => {
    setEmployee({ ...employee, [e.target.name]: e.target.value });
  };

  const handleStoreAssignmentChange = (index, field, value) => {
    const updatedAssignments = [...employee.storeAssignments];
    updatedAssignments[index][field] = value;
    setEmployee({ ...employee, storeAssignments: updatedAssignments });
  };

  const addStoreAssignment = () => {
    setEmployee({
      ...employee,
      storeAssignments: [
        ...employee.storeAssignments,
        {
          id: null, // New assignment
          store_id: "",
          role_id: null,
          hourly_rate: "",
          access_pin: "",
          employee_designation_id: "",
          status: "active",
        },
      ],
    });
  };

  const removeStoreAssignment = (index) => {
    const updatedAssignments = [...employee.storeAssignments];
    const removedAssignment = updatedAssignments[index];

    // If it's an existing assignment (has ID), add to deleted list
    let updatedDeletedAssignments = [...employee.deletedAssignments];
    if (removedAssignment.id) {
      updatedDeletedAssignments.push(removedAssignment.id);
    }

    updatedAssignments.splice(index, 1);
    setEmployee({
      ...employee,
      storeAssignments: updatedAssignments,
      deletedAssignments: updatedDeletedAssignments,
    });
  };

  const handleSubmit = async () => {
    const missingFields = [];

    if (!employee.full_name) missingFields.push("Name");
    if (!employee.email) missingFields.push("Email");
    if (!employee.language) missingFields.push("Language"); // Language is required

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in the following required fields: ${missingFields.join(
          ", ",
        )}`,
      );
      return;
    }

    updateEmployeeMutation.mutate(employee);
  };

  const handleCreateDesignation = async () => {
    if (!hasPermission("employee designations create")) {
      toast.error("You do not have permission to create designations");
      return;
    }
    if (!designationFormData.name) {
      toast.error("Designation name is required!");
      return;
    }
    createDesignationMutation.mutate(designationFormData);
  };

  const handleLanguageChange = (selectedCode) => {
    const selectedLanguage = languageSettingsData.find(
      (lang) => lang.code === selectedCode,
    );
    setEmployee({
      ...employee,
      language: selectedLanguage || "",
    });
  };

  const isLoading = updateEmployeeMutation.isPending;
  const isModalLoading = createDesignationMutation.isPending;

  if (isFetchingEmployee) {
    if (!hasPermission("employee update")) {
      return <RecordNotFound />;
    }
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Edit Employee"
          subtitle="Update employee profile and store assignments"
        />
        <PageHeaderActions>
          <Button
            variant="secondary"
            onClick={() => navigate(all_routes.users)}
          >
            <X className="mr-2 h-4 w-4" /> Cancel
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Employee Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="full_name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="full_name"
                name="full_name"
                value={employee.full_name || ""}
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                value={employee.address || ""}
                onChange={handleChange}
                placeholder="Enter address"
              />
            </div>
            <div>
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                name="email"
                value={employee.email || ""}
                onChange={handleChange}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                value={employee.phone || ""}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <Label htmlFor="language">
                Language <span className="text-destructive">*</span>
              </Label>
              <Select
                value={employee.language?.code || ""}
                onValueChange={handleLanguageChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Language" />
                </SelectTrigger>
                <SelectContent>
                  {languageSettingsData.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      {language.language}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={employee.status || ""}
                onValueChange={(val) =>
                  setEmployee({ ...employee, status: val })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {hasPermission("store employee read") && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Store Assignments</CardTitle>
            {hasPermission("store employee create") && (
              <Button onClick={addStoreAssignment} size="sm" variant="outline">
                <Plus className="mr-2 h-4 w-4" /> Add Assignment
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>
                      Store <span className="text-destructive">*</span>
                    </TableHead>
                    <TableHead>
                      Designation <span className="text-destructive">*</span>
                    </TableHead>
                    <TableHead>Access Pin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employee.storeAssignments.map((assignment, index) => {
                    const isExisting = !!assignment.id;
                    const canUpdate =
                      !isExisting || hasPermission("store employee update");
                    const canDelete =
                      !isExisting || hasPermission("store employee delete");

                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <ReactSelect
                            options={storeOptions}
                            isDisabled={!canUpdate}
                            value={
                              storeOptions.find(
                                (opt) =>
                                  opt.value ===
                                  String(assignment.store_id || ""),
                              ) || null
                            }
                            onChange={(val) =>
                              handleStoreAssignmentChange(
                                index,
                                "store_id",
                                val ? val.value : "",
                              )
                            }
                            placeholder="Select Store"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <div className="flex gap-2">
                            <div className="w-full">
                              <ReactSelect
                                options={designationOptions}
                                isDisabled={!canUpdate}
                                value={
                                  designationOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      String(
                                        assignment.employee_designation_id ||
                                        "",
                                      ),
                                  ) || null
                                }
                                onChange={(val) =>
                                  handleStoreAssignmentChange(
                                    index,
                                    "employee_designation_id",
                                    val ? val.value : "",
                                  )
                                }
                                placeholder="Select Designation"
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                            </div>
                            {hasPermission("employee designations create") &&
                              canUpdate && (
                                <Button
                                  size="icon"
                                  variant="outline"
                                  onClick={() => setShowDesignationModal(true)}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            disabled={!canUpdate}
                            value={assignment.access_pin || ""}
                            onChange={(e) =>
                              handleStoreAssignmentChange(
                                index,
                                "access_pin",
                                e.target.value,
                              )
                            }
                            placeholder="Pin"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={assignment.status || ""}
                            disabled={!canUpdate}
                            onValueChange={(val) =>
                              handleStoreAssignmentChange(index, "status", val)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          {canDelete && (
                            <Button
                              size="icon"
                              variant="destructive"
                              onClick={() => removeStoreAssignment(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {employee.storeAssignments.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center h-24 text-muted-foreground"
                      >
                        No store assignments added. Add Store Assignment to get
                        started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-muted-foreground text-sm mt-3">
              <span className="text-destructive font-bold">* Note:</span> Only
              add access pin if you want to change it, unless, keep it empty
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-4">
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            "Updating..."
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" /> Update Employee
            </>
          )}
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(all_routes.users)}
          disabled={isLoading}
        >
          <X className="mr-2 h-4 w-4" /> Cancel
        </Button>
      </div>

      <Dialog
        open={showDesignationModal}
        onOpenChange={setShowDesignationModal}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Designation</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="des-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="des-name"
                value={designationFormData.name}
                onChange={(e) =>
                  setDesignationFormData({
                    ...designationFormData,
                    name: e.target.value,
                  })
                }
                placeholder="Enter designation name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="des-desc">Description</Label>
              <Textarea
                id="des-desc"
                value={designationFormData.description}
                onChange={(e) =>
                  setDesignationFormData({
                    ...designationFormData,
                    description: e.target.value,
                  })
                }
                placeholder="Enter designation description"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="des-status">Status</Label>
              <Select
                value={designationFormData.status}
                onValueChange={(val) =>
                  setDesignationFormData({
                    ...designationFormData,
                    status: val,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleCreateDesignation}
              disabled={isModalLoading}
            >
              {isModalLoading ? (
                "Creating..."
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Create Designation
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowDesignationModal(false)}
              disabled={isModalLoading}
            >
              <X className="mr-2 h-4 w-4" /> Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default EditEmployee;
